import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
  MEMBER_TAGS,
  MODE_LABELS,
  MemberOutreachDto,
  MemberTagDto,
  STATE_LABELS,
  outreachError,
} from '../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../services/outreach.service';

/**
 * The profile dialog's "Sharon" section: whether Sharon is already talking to
 * this member, and if not, a way to start her on one cause. Starting from here
 * is the consent (docs/plans/outreach-autodrive.md, 4.12). Also the member's
 * outreach tags - do_not_approach is the one that matters most.
 *
 * Closed until asked for, like the team composer beside it, so opening a
 * profile to read it costs no extra request.
 */
@Component({
  selector: 'app-sharon-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
  ],
  template: `
    <div class="head">
      <strong>Sharon</strong>
      <button mat-icon-button (click)="closed.emit()" title="Close"><mat-icon>expand_less</mat-icon></button>
    </div>
    <p class="meta" *ngIf="loading">Loading…</p>

    <ng-container *ngIf="state as s">
      <ng-container *ngIf="s.open as c; else startForm">
        <p class="meta">
          Sharon is talking to {{ firstName || 'them' }}: {{ modeLabels[c.mode] }} ·
          {{ stateLabels[c.state] || c.state }} · {{ c.botTurns }}/{{ c.maxBotTurns }} turns.
          <a [routerLink]="['/admin/people/outreach/conversations', c.id]" mat-dialog-close>Open the conversation →</a>
        </p>
      </ng-container>
      <ng-template #startForm>
        <p class="meta" *ngIf="s.latest as l">
          Last Sharon chat: {{ l.cause }}, {{ modeLabels[l.mode] }}{{ l.closeReason ? ' (' + l.closeReason + ')' : '' }}.
          <a [routerLink]="['/admin/people/outreach/conversations', l.id]" mat-dialog-close>Open →</a>
        </p>
        <p class="meta" *ngIf="s.lastContactedAt">Last contacted by the team {{ s.lastContactedAt | date: 'd MMM yyyy' }}.</p>
        <p class="meta" *ngIf="!enabledCauses.length">
          No cause is enabled yet. Ask the copilot on the Outreach page to enable one.
        </p>
        <div class="fields" *ngIf="enabledCauses.length">
          <mat-form-field appearance="outline">
            <mat-label>Cause</mat-label>
            <mat-select [(ngModel)]="cause" (ngModelChange)="onCause()">
              <mat-option *ngFor="let c of enabledCauses" [value]="c.id">{{ c.label }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Mode</mat-label>
            <mat-select [(ngModel)]="mode">
              <mat-option value="assisted">Assisted (you approve each message)</mat-option>
              <mat-option value="auto" *ngIf="causeTrusted">Auto</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-flat-button color="primary" class="start" (click)="start()" [disabled]="busy || !cause">
            {{ busy ? 'Starting…' : 'Start Sharon chat' }}
          </button>
        </div>
      </ng-template>

      <div class="tags">
        <span class="label">Tags</span>
        <span class="tag" *ngFor="let t of tags" [title]="t.note || 'Set by ' + t.setBy">
          {{ t.tag }}
          <button type="button" (click)="removeTag(t.tag)" [disabled]="busy" [attr.aria-label]="'Remove ' + t.tag">×</button>
        </span>
        <select class="add" [(ngModel)]="newTag" [disabled]="busy || !freeTags.length" aria-label="Add a tag">
          <option value="">Add tag…</option>
          <option *ngFor="let t of freeTags" [value]="t">{{ t }}</option>
        </select>
        <button mat-button (click)="addTag()" [disabled]="busy || !newTag">Add</button>
      </div>
    </ng-container>

    <p class="line error" *ngIf="error">{{ error }}</p>
    <p class="line notice" *ngIf="notice">{{ notice }}</p>
  `,
  styles: [
    `
      :host { display: block; }
      .head { display: flex; align-items: center; justify-content: space-between; color: #12332f; margin-bottom: 6px; }
      .meta { margin: 0 0 8px; font-size: 12.5px; color: #5e5a57; line-height: 1.5; }
      .meta a { color: #1f6f66; font-weight: 600; }
      .fields { display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; }
      .fields mat-form-field { width: 240px; }
      .tags { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; font-size: 12.5px; margin-top: 4px; }
      .tags .label { color: #8f8c88; margin-right: 4px; }
      .tag { background: rgba(18, 51, 47, 0.06); color: #12332f; border-radius: 999px; padding: 3px 4px 3px 10px; font-weight: 600; }
      .tag button { background: none; border: none; cursor: pointer; color: #5e5a57; font-size: 14px; line-height: 1; padding: 0 4px; }
      .add { border: 1px solid rgba(18, 51, 47, 0.14); border-radius: 999px; padding: 4px 8px; font: inherit; background: #fff; }
      .line { margin: 6px 0 0; font-size: 13px; line-height: 1.5; }
      .line.error { color: #a62639; }
      .line.notice { color: #1f6f66; }
    `,
  ],
})
export class SharonPanelComponent implements OnInit {
  @Input({ required: true }) userId!: string;
  @Input() firstName: string | null | undefined = null;
  @Output() closed = new EventEmitter<void>();

  readonly modeLabels = MODE_LABELS;
  readonly stateLabels = STATE_LABELS;

  state: MemberOutreachDto | null = null;
  tags: MemberTagDto[] = [];
  loading = true;
  busy = false;
  error: string | null = null;
  notice: string | null = null;

  cause = '';
  mode: 'assisted' | 'auto' = 'assisted';
  newTag = '';

  constructor(private outreach: OutreachService) {}

  ngOnInit(): void {
    this.outreach.memberOutreach(this.userId).subscribe({
      next: (s) => {
        this.loading = false;
        this.state = s;
        this.tags = s.tags;
        this.cause = this.enabledCauses[0]?.id ?? '';
      },
      error: (err) => {
        this.loading = false;
        this.error = outreachError(err, 'Could not read Sharon’s state for this member');
      },
    });
  }

  get enabledCauses(): { id: string; label: string; trusted: boolean }[] {
    return (this.state?.causes ?? []).filter((c) => c.enabled);
  }

  /** Auto is only offered for a cause trusted with it. */
  get causeTrusted(): boolean {
    return Boolean(this.enabledCauses.find((c) => c.id === this.cause)?.trusted);
  }

  get freeTags(): string[] {
    const have = new Set(this.tags.map((t) => t.tag));
    return MEMBER_TAGS.filter((t) => !have.has(t));
  }

  onCause(): void {
    if (!this.causeTrusted) this.mode = 'assisted';
  }

  start(): void {
    if (!this.cause || this.busy) return;
    this.busy = true;
    this.error = null;
    this.notice = null;
    this.outreach.startForMember(this.userId, this.cause, this.mode).subscribe({
      next: ({ conversation }) => {
        this.busy = false;
        if (this.state) this.state = { ...this.state, open: conversation };
        this.notice =
          conversation.mode === 'assisted'
            ? 'Started. Sharon drafts the opener for you to approve; you get an email when it is ready.'
            : 'Started on Auto. Sharon sends the opener at the next paced slot.';
      },
      error: (err) => {
        this.busy = false;
        this.error = outreachError(err, 'Could not start');
      },
    });
  }

  addTag(): void {
    if (!this.newTag || this.busy) return;
    this.tagCall(this.outreach.setTag(this.userId, this.newTag));
    this.newTag = '';
  }

  removeTag(tag: string): void {
    if (!this.busy) this.tagCall(this.outreach.removeTag(this.userId, tag));
  }

  private tagCall(call: ReturnType<OutreachService['tags']>): void {
    this.busy = true;
    this.error = null;
    call.subscribe({
      next: ({ tags }) => {
        this.busy = false;
        this.tags = tags;
      },
      error: (err) => {
        this.busy = false;
        this.error = outreachError(err, 'Could not change the tags');
      },
    });
  }
}
